import axios from './axios';
import { PubSub } from '@/services/pub-sub';
import { store } from '@/main';
import router from '@/router';
import { buildOAuthLogoutUrl } from '@/services/oauth';
// import Vue from 'vue';

const key = Symbol('key');
const keyEnforcer = Symbol('keyEnforcer');
const tokenStorageKey = 'user-token';
const userStorageKey = 'user-data';

class UserService extends PubSub {
  _user = null;

  constructor (enforcer) {
    super();
    if (enforcer !== keyEnforcer) {
      throw new Error('Instantiation failed: use UserService.instance instead of new.');
    }

    this._token = localStorage.getItem(tokenStorageKey) || null;

    const storedUserData = localStorage.getItem(userStorageKey);

    if (storedUserData !== null) {
      this._user = JSON.parse(storedUserData);
    } else {
      this.logout();
      // Vue.prototype.$flash_warning('Произошла ошибка с разрешением прав');
    }
  }

  static get instance () {
    if (!this[key]) {
      this[key] = new UserService(keyEnforcer);
    }

    return this[key];
  }

  static set instance (v) {
    throw new Error('Can\'t change constant property!');
  }

  get token () {
    return this._token;
  }

  get isAuth () {
    return this._token !== null;
  }

  set token (token) {
    this._token = token;
    localStorage.setItem(tokenStorageKey, token);
  }

  get user () {
    return this._user;
  }

  set user (user) {
    this._user = Object.freeze(user);
    localStorage.setItem(userStorageKey, JSON.stringify(user));
  }

  logout ({ sso = false } = {}) {
    // Сохраняем текущий путь даже при обновлении страницы
    const fallbackFullPath = window.location.pathname + window.location.search + window.location.hash;
    const currentPath = router.currentRoute.fullPath !== '/' ? router.currentRoute.fullPath : fallbackFullPath;
    if (window.location.pathname !== '/auth/callback') {
      store.dispatch('saveRedirectAfterLogin', currentPath).catch(err => { console.error(err); });
    }

    const shouldSsoLogout = sso && localStorage.getItem('evo-id-login') === '1';
    const refreshToken = localStorage.getItem('oauth-refresh-token');
    const ssoLogoutUrl = shouldSsoLogout ? buildOAuthLogoutUrl() : null;

    if (shouldSsoLogout && refreshToken) {
      axios.post('/auth/oauth/revoke', { refresh_token: refreshToken }).catch(() => {});
    }

    this._token = null;
    store.commit('setLogout');
    localStorage.clear();
    this.publish('logout');

    if (ssoLogoutUrl) {
      window.location.href = ssoLogoutUrl;
    }
  }

  /**
   * Отправляет запрос на регистрацию нового пользователя
   *
   * @param string login
   * @param string password
   * @param string password_confirmation
   * @returns {Promise<any>}
   */
  // registration ({ login, password, password_confirmation }) {
  //   return new Promise((resolve, reject) => {
  //     axios.post('/auth/register', {
  //       login, password, password_confirmation,
  //     })
  //       .then(resolve)
  //       .catch(reject);
  //   });
  // }

  /**
   * Отправляет запрос на авторизацию в системе
   *
   * @param string login
   * @param string password
   * @returns {Promise<any>}
   */
  auth ({ email, password }) {
    return new Promise((resolve, reject) => {
      axios.post('/auth/login', {
        email, password,
      })
        .then(({ data }) => {
          if (data.status === false) {
            throw new Error('Неверная почта или пароль');
          }
          this.applySession(data);
          resolve(data);
        })
        .catch(reject);
    });
  }

  /**
   * Обменивает authorization code ЭВО ID на локальную сессию
   *
   * @returns {Promise<any>}
   */
  authEvoId ({ code, code_verifier, redirect_uri }) {
    return axios.post('/auth/oauth', {
      code,
      code_verifier,
      redirect_uri,
    })
      .then(({ data }) => {
        this.applySession(data, { oauth: true });
        return data;
      });
  }

  applySession (data, { oauth = false } = {}) {
    this.token = data.token;
    this.user = data.user;
    localStorage.setItem('role', data.user.role_id);
    localStorage.setItem('name', data.user.name);
    localStorage.setItem('roleName', data.user.role.name);
    localStorage.setItem('userId', data.user.id);
    if (oauth) {
      localStorage.setItem('evo-id-login', '1');
      if (data.refresh_token) {
        localStorage.setItem('oauth-refresh-token', data.refresh_token);
      }
    }
    store.commit('setRole', data.user.role_id);
    store.commit('setName', data.user.name);
    store.commit('setRoleName', data.user.role.name);
    store.commit('setUserId', data.user.id);
    this.publish('login');
  }

  /**
   * Возвращает информацию о текущем пользователе
   *
   * @returns {Promise<any>}
   */
  me () {
    return new Promise((resolve, reject) => {
      axios.get('/auth/me')
        .then(({ data }) => resolve(data))
        .catch(reject);
    });
  }

  /**
   * Обновляет токен текущего пользователя
   *
   * @returns {Promise<any>}
   */
  refresh () {
    if (localStorage.getItem('evo-id-login') === '1') {
      return axios.post('/auth/oauth/refresh', {
        refresh_token: localStorage.getItem('oauth-refresh-token'),
      }).then((response) => {
        if (response.data && response.data.refresh_token) {
          localStorage.setItem('oauth-refresh-token', response.data.refresh_token);
        }
        return response;
      });
    }

    return new Promise((resolve, reject) => {
      axios.get('/auth/refresh')
        .then((response) => {
          resolve(response);
        })
        .catch(reject);
    });
  }
}

export default UserService;
